/**
 * SongbookPage.jsx — TurfPulse Vol. 1 · Five-song hub
 * Route: /about/the-songbook
 * Public, indexable. Built for sharing with musicians (Suno, friends, the band).
 */
import React, { useState, useMemo, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useLocation } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { ArrowLeft, Music, Disc3, Copy, Printer, ExternalLink, ChevronDown } from 'lucide-react';
import { SONGBOOK, SONGBOOK_META } from '../data/songbook';

const ACCENT_TONES = {
  amber:   { bg: 'bg-amber-50 dark:bg-amber-950/30',   ring: 'border-amber-300 dark:border-amber-800',   text: 'text-amber-700 dark:text-amber-300',   pill: 'bg-amber-500 text-amber-950' },
  violet:  { bg: 'bg-violet-50 dark:bg-violet-950/30', ring: 'border-violet-300 dark:border-violet-800', text: 'text-violet-700 dark:text-violet-300', pill: 'bg-violet-500 text-violet-50' },
  rose:    { bg: 'bg-rose-50 dark:bg-rose-950/30',     ring: 'border-rose-300 dark:border-rose-800',     text: 'text-rose-700 dark:text-rose-300',     pill: 'bg-rose-500 text-rose-50' },
  emerald: { bg: 'bg-emerald-50 dark:bg-emerald-950/30', ring: 'border-emerald-300 dark:border-emerald-800', text: 'text-emerald-700 dark:text-emerald-300', pill: 'bg-emerald-500 text-emerald-50' },
  sky:     { bg: 'bg-sky-50 dark:bg-sky-950/30',       ring: 'border-sky-300 dark:border-sky-800',       text: 'text-sky-700 dark:text-sky-300',       pill: 'bg-sky-500 text-sky-50' },
};

// Build a plain-text version of the song for copying / sharing
function songToPlainText(song) {
  const lines = [];
  lines.push(`${song.title.toUpperCase()}`);
  lines.push(song.subtitle);
  lines.push(`Genre: ${song.genre}`);
  lines.push('');
  lines.push('--- TRACK SPECS ---');
  song.specs.forEach(([k, v]) => lines.push(`${k}: ${v}`));
  lines.push('');
  lines.push('--- PRODUCTION ---');
  song.production.forEach(([k, v]) => lines.push(`${k}: ${v}`));
  lines.push('');
  lines.push('--- ARRANGEMENT ---');
  lines.push(song.arrangement);
  lines.push('');
  lines.push('--- CHORDS ---');
  Object.entries(song.chords).forEach(([k, v]) => lines.push(`${k}:  ${v}`));
  lines.push('');
  lines.push('--- LYRICS ---');
  song.sections.forEach((sec) => {
    lines.push('');
    lines.push(`[${sec.label}]`);
    if (sec.chords) lines.push(`  ${sec.chords}`);
    lines.push('');
    sec.lines.forEach((l) => lines.push(l));
  });
  lines.push('');
  if (song.scripture) {
    lines.push(`--- FOUNDATION ---`);
    lines.push(song.scripture);
    lines.push('');
  }
  lines.push(`— TurfPulse Songbook · ${song.title}`);
  return lines.join('\n');
}

const SongCard = ({ song, isOpen, onToggle, embedUrl, setEmbedUrl }) => {
  const tone = ACCENT_TONES[song.accent] || ACCENT_TONES.amber;

  const copyAll = () => {
    const txt = songToPlainText(song);
    navigator.clipboard.writeText(txt).then(
      () => toast.success(`Copied "${song.title}" — full lyrics + chords`),
      () => toast.error('Copy failed — try long-press to select instead'),
    );
  };

  const copyChords = () => {
    const lines = [`${song.title} — chord chart`];
    Object.entries(song.chords).forEach(([k, v]) => lines.push(`${k}:  ${v}`));
    navigator.clipboard.writeText(lines.join('\n')).then(
      () => toast.success('Chord chart copied'),
      () => toast.error('Copy failed'),
    );
  };

  const shareUrl = `${typeof window !== 'undefined' ? window.location.origin : ''}/about/the-songbook#${song.id}`;
  const copyShareUrl = () => {
    navigator.clipboard.writeText(shareUrl).then(
      () => toast.success('Share link copied'),
      () => toast.error('Copy failed'),
    );
  };

  return (
    <Card
      id={song.id}
      className={`scroll-mt-24 ${tone.ring} ${song.foundation ? 'border-2 ring-2 ring-amber-200 dark:ring-amber-900/60' : 'border'}`}
      data-testid={`songbook-song-${song.id}`}
    >
      <button
        type="button"
        onClick={onToggle}
        className={`w-full text-left ${tone.bg} hover:opacity-95 rounded-t-lg`}
        data-testid={`songbook-toggle-${song.id}`}
      >
        <CardHeader>
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="flex items-start gap-3">
              <span className="text-3xl md:text-4xl select-none mt-0.5">{song.emoji}</span>
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs uppercase tracking-widest font-bold opacity-60">
                    Track 0{song.number}
                  </span>
                  <Badge className={`${tone.pill} text-[10px] uppercase tracking-wider`}>
                    {song.genre}
                  </Badge>
                  {song.foundation && (
                    <Badge className="bg-amber-500 text-amber-950 text-[10px] uppercase tracking-wider">
                      🙏 Foundation
                    </Badge>
                  )}
                  {song.unreleased && (
                    <Badge className="bg-zinc-700 text-amber-200 text-[10px] uppercase tracking-wider" data-testid={`songbook-unreleased-${song.id}`}>
                      🤫 Coming soon · sneak peek
                    </Badge>
                  )}
                </div>
                <CardTitle className="text-2xl md:text-3xl mt-1">{song.title}</CardTitle>
                <p className={`text-sm mt-1 ${tone.text} italic`}>{song.subtitle}</p>
                {song.rollout && (
                  <p className="text-xs mt-2 text-muted-foreground">📅 Rollout: <span className="font-semibold">{song.rollout}</span></p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <ChevronDown className={`w-5 h-5 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
            </div>
          </div>
        </CardHeader>
      </button>

      {isOpen && (
        <CardContent className="space-y-6 pt-4" data-testid={`songbook-content-${song.id}`}>
          {/* Action bar */}
          <div className="flex flex-wrap gap-2 -mt-2">
            <Button size="sm" variant="outline" onClick={copyAll} data-testid={`songbook-copy-all-${song.id}`}>
              <Copy className="w-3.5 h-3.5 mr-1.5" /> Copy full song
            </Button>
            <Button size="sm" variant="outline" onClick={copyChords} data-testid={`songbook-copy-chords-${song.id}`}>
              <Music className="w-3.5 h-3.5 mr-1.5" /> Copy chords only
            </Button>
            <Button size="sm" variant="outline" onClick={copyShareUrl} data-testid={`songbook-copy-link-${song.id}`}>
              <ExternalLink className="w-3.5 h-3.5 mr-1.5" /> Copy share link
            </Button>
          </div>

          {/* Scripture (foundation track only) */}
          {song.scripture && (
            <div className="p-4 rounded-md bg-amber-50/60 dark:bg-amber-950/20 border-l-4 border-amber-500">
              <p className="text-sm italic font-serif">{song.scripture}</p>
            </div>
          )}

          {/* Embed slot */}
          <div className="border-2 border-dashed rounded-md p-4 bg-card">
            <div className="text-xs uppercase tracking-widest text-muted-foreground font-bold mb-2">🎵 Listen</div>
            {embedUrl ? (
              <div className="aspect-video w-full">
                <iframe
                  title={song.title}
                  src={embedUrl}
                  className="w-full h-full rounded-md"
                  allow="autoplay; clipboard-write; encrypted-media; picture-in-picture"
                  allowFullScreen
                  data-testid={`songbook-embed-iframe-${song.id}`}
                />
              </div>
            ) : (
              <div className="text-center py-6 px-4">
                <Disc3 className="w-10 h-10 mx-auto mb-2 text-muted-foreground/60 animate-pulse" />
                <p className="text-sm text-muted-foreground mb-3">Paste a Suno / Udio / YouTube / Spotify embed URL to mount the player.</p>
                <input
                  type="url"
                  placeholder="https://suno.com/embed/… or https://www.youtube.com/embed/…"
                  className="w-full max-w-md mx-auto px-3 py-2 rounded-md border bg-background text-sm"
                  onChange={(e) => setEmbedUrl(song.id, e.target.value.trim())}
                  data-testid={`songbook-embed-input-${song.id}`}
                />
              </div>
            )}
          </div>

          {/* Track specs + Production */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <h3 className="text-sm uppercase tracking-widest font-bold mb-2">🎚 Track Specs</h3>
              <dl className="text-sm space-y-1">
                {song.specs.map(([k, v]) => (
                  <div key={k} className="flex gap-2 border-b border-border/50 py-1">
                    <dt className="font-semibold w-20 shrink-0">{k}</dt>
                    <dd className="text-muted-foreground">{v}</dd>
                  </div>
                ))}
              </dl>
            </div>
            <div>
              <h3 className="text-sm uppercase tracking-widest font-bold mb-2">🎬 Production Map</h3>
              <dl className="text-sm space-y-1">
                {song.production.map(([k, v]) => (
                  <div key={k} className="flex gap-2 border-b border-border/50 py-1">
                    <dt className="font-semibold w-20 shrink-0">{k}</dt>
                    <dd className="text-muted-foreground">{v}</dd>
                  </div>
                ))}
              </dl>
            </div>
          </div>

          {/* Arrangement */}
          <div>
            <h3 className="text-sm uppercase tracking-widest font-bold mb-2">📐 Arrangement</h3>
            <p className="text-sm text-muted-foreground italic leading-relaxed">{song.arrangement}</p>
          </div>

          {/* Chords */}
          <div>
            <h3 className="text-sm uppercase tracking-widest font-bold mb-2">🎸 Chord Chart</h3>
            <div className="space-y-1 text-sm font-mono bg-muted/50 rounded-md p-3 overflow-x-auto">
              {Object.entries(song.chords).map(([k, v]) => (
                <div key={k} className="whitespace-nowrap">
                  <span className="font-bold capitalize text-foreground">{k.replace(/([A-Z])/g, ' $1')}:</span>{' '}
                  <span className="text-muted-foreground">{v}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Lyrics + per-section chords */}
          <div>
            <h3 className="text-sm uppercase tracking-widest font-bold mb-2">🎤 Lyrics &amp; Section Chords</h3>
            <div className="space-y-4">
              {song.sections.map((sec, i) => (
                <div
                  key={i}
                  className={`rounded-md p-3 ${sec.type === 'hook' ? `${tone.bg} border ${tone.ring}` : 'bg-muted/30'}`}
                  data-testid={`songbook-section-${song.id}-${i}`}
                >
                  <div className="flex items-center justify-between gap-2 flex-wrap mb-1">
                    <span className="text-xs uppercase tracking-widest font-bold">{sec.label}</span>
                    {sec.chords && (
                      <code className="text-[10px] font-mono bg-background px-2 py-0.5 rounded border">
                        {sec.chords}
                      </code>
                    )}
                  </div>
                  <pre className={`whitespace-pre-line font-sans text-sm leading-relaxed ${sec.type === 'speak' ? 'italic text-muted-foreground' : ''} ${sec.type === 'hook' ? 'font-semibold' : ''}`}>
                    {sec.lines.join('\n')}
                  </pre>
                </div>
              ))}
            </div>
          </div>

          {/* Suggested caption */}
          {song.caption && (
            <div className="border-t pt-4">
              <h3 className="text-sm uppercase tracking-widest font-bold mb-2">📤 Suggested FB / IG Caption</h3>
              <div className="text-sm bg-muted/50 rounded-md p-3 font-mono leading-relaxed">{song.caption}</div>
              <Button
                size="sm"
                variant="outline"
                className="mt-2"
                onClick={() => {
                  navigator.clipboard.writeText(song.caption);
                  toast.success('Caption copied');
                }}
                data-testid={`songbook-copy-caption-${song.id}`}
              >
                <Copy className="w-3.5 h-3.5 mr-1.5" /> Copy caption
              </Button>
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
};

const SongbookPage = () => {
  const { hash } = useLocation();

  const [openIds, setOpenIds] = useState(() => {
    // Fall back to window.location.hash because router's useLocation().hash
    // can be empty on the first render when the user navigates directly to
    // /about/the-songbook#soak-it-up. Lazy initializer captures whichever is set.
    const h = (hash || (typeof window !== 'undefined' ? window.location.hash : '') || '').replace('#', '');
    return new Set(h ? [h] : []);
  });
  const [embeds, setEmbeds] = useState({});

  // Re-sync openIds whenever the hash changes (back/forward, in-page anchors).
  useEffect(() => {
    const h = (hash || '').replace('#', '');
    if (h) {
      setOpenIds((prev) => (prev.has(h) ? prev : new Set([...prev, h])));
      // Smooth-scroll to the song after expansion settles.
      setTimeout(() => {
        const el = document.getElementById(h);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 60);
    }
  }, [hash]);

  const toggleSong = (id) => {
    setOpenIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const expandAll = () => setOpenIds(new Set(SONGBOOK.map((s) => s.id)));
  const collapseAll = () => setOpenIds(new Set());

  const setSongEmbed = (id, url) => {
    setEmbeds((prev) => ({ ...prev, [id]: url }));
  };

  // MusicAlbum schema for Google Music carousel eligibility
  const albumLd = useMemo(() => ({
    '@context': 'https://schema.org',
    '@type': 'MusicAlbum',
    name: SONGBOOK_META.title,
    description: SONGBOOK_META.subtitle,
    byArtist: {
      '@type': 'MusicGroup',
      name: 'TurfPulse',
    },
    numTracks: SONGBOOK.length,
    track: SONGBOOK.map((s) => ({
      '@type': 'MusicRecording',
      name: s.title,
      genre: s.genre,
      position: s.number,
    })),
  }), []);

  return (
    <>
      <Helmet>
        <title>TurfPulse Songbook · Vol. 1</title>
        <meta name="description" content={SONGBOOK_META.subtitle} />
        <meta property="og:title" content={SONGBOOK_META.title} />
        <meta property="og:description" content={SONGBOOK_META.subtitle} />
        <script type="application/ld+json">{JSON.stringify(albumLd)}</script>
      </Helmet>

      <main className="min-h-screen bg-gradient-to-br from-amber-50 via-rose-50 to-emerald-50 dark:from-zinc-950 dark:via-amber-950/30 dark:to-emerald-950/40 pb-20">
        <div className="container mx-auto max-w-5xl px-4 py-8">
          <div className="mb-6 flex items-center justify-between gap-3 flex-wrap">
            <Button variant="ghost" size="sm" asChild data-testid="songbook-back-button">
              <Link to="/about"><ArrowLeft className="w-4 h-4 mr-1" /> Back to About</Link>
            </Button>
            <div className="flex gap-2 print:hidden">
              <Button size="sm" variant="outline" onClick={expandAll} data-testid="songbook-expand-all">Expand all</Button>
              <Button size="sm" variant="outline" onClick={collapseAll} data-testid="songbook-collapse-all">Collapse all</Button>
              <Button size="sm" variant="outline" onClick={() => window.print()} data-testid="songbook-print">
                <Printer className="w-3.5 h-3.5 mr-1.5" /> Print
              </Button>
            </div>
          </div>

          {/* Hero */}
          <div className="text-center mb-10" data-testid="songbook-hero">
            <Badge className="bg-amber-500 hover:bg-amber-600 text-amber-950 mb-4 uppercase tracking-widest text-[10px]">
              <Disc3 className="w-3 h-3 mr-1" /> The TurfPulse Songbook
            </Badge>
            <h1 className="text-5xl md:text-7xl font-black tracking-tight bg-gradient-to-r from-amber-600 via-rose-500 to-emerald-600 bg-clip-text text-transparent">
              {SONGBOOK_META.title}
            </h1>
            <p className="mt-4 text-lg md:text-xl text-muted-foreground italic max-w-2xl mx-auto">
              {SONGBOOK_META.subtitle}
            </p>
            <p className="mt-2 text-sm text-muted-foreground max-w-2xl mx-auto">
              {SONGBOOK_META.byline}
            </p>
          </div>

          {/* Foundation scripture */}
          <Card className="mb-8 border-2 border-amber-300 dark:border-amber-800 bg-amber-50/60 dark:bg-amber-950/20" data-testid="songbook-foundation">
            <CardContent className="pt-6">
              <div className="text-center">
                <Badge className="bg-amber-500 text-amber-950 mb-3 uppercase tracking-widest text-[10px]">🙏 Foundation</Badge>
                <p className="text-base md:text-lg font-serif italic leading-relaxed max-w-3xl mx-auto">
                  {SONGBOOK_META.scripture}
                </p>
                <p className="mt-3 text-xs uppercase tracking-widest text-muted-foreground">
                  Without the Lord, there is no harvest. All glory to the Most High.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Track index */}
          <div className="mb-6 p-4 rounded-lg bg-card border" data-testid="songbook-tracklist">
            <h2 className="text-sm uppercase tracking-widest font-bold mb-3">📋 Tracklist · {SONGBOOK.length} songs</h2>
            <ol className="space-y-1">
              {SONGBOOK.map((s) => {
                const tone = ACCENT_TONES[s.accent] || ACCENT_TONES.amber;
                return (
                  <li key={s.id} className="flex items-center gap-3 text-sm">
                    <span className="font-mono text-muted-foreground w-6">0{s.number}</span>
                    <span className="text-2xl">{s.emoji}</span>
                    <a
                      href={`#${s.id}`}
                      onClick={() => setOpenIds((prev) => new Set([...prev, s.id]))}
                      className="font-semibold hover:underline"
                      data-testid={`songbook-track-link-${s.id}`}
                    >
                      {s.title}
                    </a>
                    <Badge variant="outline" className={`text-[9px] uppercase ${tone.text}`}>{s.genre}</Badge>
                    {s.foundation && <span className="text-amber-500 text-xs">🙏</span>}
                  </li>
                );
              })}
            </ol>
          </div>

          {/* Songs */}
          <div className="space-y-4">
            {SONGBOOK.map((song) => (
              <SongCard
                key={song.id}
                song={song}
                isOpen={openIds.has(song.id)}
                onToggle={() => toggleSong(song.id)}
                embedUrl={embeds[song.id]}
                setEmbedUrl={setSongEmbed}
              />
            ))}
          </div>

          {/* Footer */}
          <div className="mt-12 text-center text-sm text-muted-foreground space-y-2" data-testid="songbook-footer">
            <p>🌱 Built by Derwin · Round Rock, TX · TurfPulse Vol. 1</p>
            <p className="italic">
              <em>"He makes it grow."</em> — 1 Corinthians 3:7
            </p>
            <p>
              <Link to="/about" className="underline hover:text-emerald-600">← About</Link>
              {' · '}
              <Link to="/" className="underline hover:text-emerald-600">TurfPulse Home</Link>
            </p>
          </div>
        </div>
      </main>
    </>
  );
};

export default SongbookPage;
